// M3 galaxy generator tuning: schema and loader. Kept out of schemas.ts and index.ts so the game
// bundle does not carry the galaxy config until something in the game uses it
// (see docs/plans/2026-09-26-m3-galaxy-generator-prototype.md).
import { z } from 'zod'
import galaxyJson from './galaxy.json'

const positive = z.number().positive()

export const galaxyRegionIds = ['gate', 'mid', 'deep', 'rim'] as const

export const galaxyStarClasses = ['O', 'B', 'A', 'F', 'G', 'K', 'M'] as const
export const galaxyArchetypes = [
  'mining-belt',
  'ice-field',
  'gas-harvest',
  'industrial-hub',
  'trade-crossroads',
  'derelict-graveyard',
  'raider-haven',
  'descendant-enclave',
  'tetherstone-vein',
  'chokepoint',
] as const
export const galaxyPoiKinds = ['derelict', 'cache', 'anomaly', 'hidden-base', 'nav-beacon'] as const
export const galaxyAnchorIds = ['home-gate', 'authority-seat', 'megacorp-hq', 'descendant-capital', 'guardian-base', 'okafor-hideout'] as const
/** Canonical order; a system's reasons are listed in this order. */
export const galaxyReasons = ['market-specialty', 'resource', 'story', 'contract-hub'] as const
/** Raw ores (M2 ores plus the M4 raw tier, D-032). */
export const galaxyOres = ['ferrite', 'silicates', 'ice', 'titanium', 'platinum', 'tetherstone', 'refractory_ore'] as const
const galaxyFieldKinds = ['asteroid', 'ice', 'gas'] as const
/** The Mk III ore (5(e), MC-e2) and the regions it never forms in: no gate and no mid (deep and rim only). */
export const galaxyOuterOnly = { ore: 'refractory_ore', regions: ['gate', 'mid'] } as const satisfies { ore: (typeof galaxyOres)[number]; regions: readonly (typeof galaxyRegionIds)[number][] }
/** The most an anchor's trace ore may weigh in one field (D-037: the home gate's trace tetherstone). */
export const galaxyTraceMaxWeight = 0.03

/** An inclusive [min, max] integer range. */
const galaxyIntRange = z
  .tuple([z.number().int().nonnegative(), z.number().int().nonnegative()])
  .refine(([lo, hi]) => lo <= hi, { message: 'range min must not exceed max' })
/** A [min, max] real range. */
const galaxyNumRange = z
  .tuple([z.number().nonnegative(), z.number().nonnegative()])
  .refine(([lo, hi]) => lo <= hi, { message: 'range min must not exceed max' })
const galaxyWeight = z.number().nonnegative()
const galaxyReasonList = z.array(z.enum(galaxyReasons))
const galaxyOreWeights = z.partialRecord(z.enum(galaxyOres), galaxyWeight)
const galaxyPoiCounts = z.partialRecord(z.enum(galaxyPoiKinds), z.number().int().nonnegative())

/** A key's share of the total weight (0 when every weight is 0). */
function galaxyShare(weights: Record<string, number>, key: string): number {
  const total = Object.values(weights).reduce((s, w) => s + w, 0)
  return total > 0 ? (weights[key] ?? 0) / total : 0
}

/** Deposit grades, poorest first (M8 5(a), R1 §5.2 Layer 3). The index matches `minerals.grades.mult` and `.weights`. */
export const galaxyGradeIds = ['poor', 'standard', 'rich', 'pristine'] as const
export type GalaxyGradeId = (typeof galaxyGradeIds)[number]
const galaxyOreList = z
  .array(z.enum(galaxyOres))
  .min(1)
  .refine((os) => new Set(os).size === os.length, { message: 'ore lists must not repeat an ore' })

/**
 * The M8 5(a) regional mineral identity (MC-a1, D-TBD T18; R1 §5.2). Nothing here moves `oreWeights`: the generator
 * post-pass (MC-a2a) writes a separate `mineralWeights`, and the strategic sim reads it only behind `strategicGrades`.
 * `stackCap` is a runtime clamp on the final per-ore share multiplier (signature × hotspot) relative to the region band,
 * applied by MC-a2a and MC-a9; the schema only checks that each single factor fits under it. Grade scales ore mass and
 * strategic reserves, not the share, so it sits outside the clamp.
 */
const galaxyMineralsSchema = z
  .object({
    comment: z.string().optional(),
    /** The strategic sim reads `mineralWeights` × grade (MC-a4). Off: the sim is byte-identical. */
    strategicGrades: z.boolean(),
    /** Per-region asteroid mix; replaces `fields.ores.asteroid` in `mineralWeights` only. Ice and gas fields keep theirs. */
    regionBands: z.record(z.enum(galaxyRegionIds), galaxyOreWeights),
    signature: z.object({
      mult: positive,
      secondaryChance: z.number().min(0).max(1),
      secondaryMult: positive,
      /** The ores a system in each region may draw as its signature. */
      eligible: z.record(z.enum(galaxyRegionIds), galaxyOreList),
      /** A weak flavour bias on the signature mix by star class (D-TBD T19); an absent class or ore counts 1. */
      starBias: z.partialRecord(z.enum(galaxyStarClasses), z.partialRecord(z.enum(galaxyOres), positive)),
    }),
    grades: z.object({
      ids: z
        .array(z.enum(galaxyGradeIds))
        .refine((ids) => ids.length === galaxyGradeIds.length && ids.every((id, i) => id === galaxyGradeIds[i]), {
          message: 'grades.ids must be poor, standard, rich, pristine',
        }),
      /** Multiplier on ore mass per rock and on strategic reserve size and regrowth, by grade. */
      mult: z
        .array(positive)
        .length(galaxyGradeIds.length)
        .refine((m) => m.every((v, i) => i === 0 || v >= m[i - 1]), { message: 'grade multipliers must not fall' }),
      /** Roll weights per region, by grade. */
      weights: z.record(
        z.enum(galaxyRegionIds),
        z
          .array(galaxyWeight)
          .length(galaxyGradeIds.length)
          .refine((w) => w.some((v) => v > 0), { message: 'every region needs a positive grade weight' }),
      ),
    }),
    /** Anomaly hotspots concentrate the signature ore (MC-a9). */
    hotspot: z.object({ mult: positive }),
    stackCap: z.number().min(1),
    /** Ores a system of an archetype never carries in its mineral mix or signature (5(e): no refractory_ore in a tetherstone vein). */
    archetypeExclude: z.partialRecord(z.enum(galaxyArchetypes), galaxyOreList),
    /**
     * The Mk III ore's own roll (5(e) MC-e2, v7 migration calls §7): a separate `minerals-refractory` stream after the M8
     * rolls, so a v6 world's signatures and grades never re-roll. Per region, the chance a system's asteroid fields carry it
     * as a signature (× its star bias, capped at 1); its share is its band share × (signature.mult if so) × star bias,
     * capped at stackCap × band, and the old ores' shares only scale to make room.
     */
    refractory: z.object({ signatureChance: z.partialRecord(z.enum(galaxyRegionIds), z.number().min(0).max(1)) }),
    /** Signature systems the guarantee pass (MC-a3) ensures at the start. */
    guarantee: z.object({
      /** Titanium-signature mid systems reachable online from home. */
      midTitanium: z.number().int().nonnegative(),
      /** Platinum-signature deep systems with an online path. */
      deepPlatinum: z.number().int().nonnegative(),
      /** 5(e) MC-e6b: refractory-signature deep systems with an online path (never a vein system; absent = 0). */
      deepRefractory: z.number().int().nonnegative().optional(),
    }),
    /** Interdiction-rate multiplier by the richest local field's grade (MC-a9 caps it). */
    dangerByGrade: z.record(z.enum(galaxyGradeIds), positive),
  })
  .superRefine((m, ctx) => {
    const issue = (message: string): void => {
      ctx.addIssue({ code: 'custom', message })
    }
    const hasTether = (ws: Partial<Record<string, number>> | undefined): boolean => ws?.tetherstone !== undefined
    if (
      galaxyRegionIds.some((r) => hasTether(m.regionBands[r]) || m.signature.eligible[r].includes('tetherstone')) ||
      Object.values(m.signature.starBias).some(hasTether)
    ) {
      issue('tetherstone never enters the minerals block (bands, signature lists, star bias)')
    }
    for (const r of galaxyRegionIds) {
      const band = m.regionBands[r]
      if (!((band.ferrite ?? 0) > 0 && (band.silicates ?? 0) > 0)) issue(`minerals.regionBands.${r}: ferrite and silicates must stay above 0`)
      if (!((band.ice ?? 0) > 0)) issue(`minerals.regionBands.${r}: ice must stay above 0 (fuel cells need it)`)
    }
    // 5(e) geology (MC-e2, D-360 D4, docs/story/setting.md): the Mk III ore is deep and rim only and never in a vein system.
    for (const r of galaxyOuterOnly.regions) {
      if ((m.regionBands[r][galaxyOuterOnly.ore] ?? 0) > 0 || m.signature.eligible[r].includes(galaxyOuterOnly.ore)) {
        issue(`minerals: ${galaxyOuterOnly.ore} never forms in the ${r} region (bands, signature lists)`)
      }
    }
    for (const r of galaxyRegionIds) {
      if (m.signature.eligible[r].includes(galaxyOuterOnly.ore)) {
        issue(`minerals.signature.eligible.${r}: ${galaxyOuterOnly.ore} rolls on its own stream (minerals.refractory), not here`)
      }
    }
    for (const r of galaxyOuterOnly.regions) {
      if ((m.refractory.signatureChance[r] ?? 0) > 0) issue(`minerals.refractory.signatureChance.${r} must be 0 (deep and rim only)`)
    }
    if (!(m.archetypeExclude['tetherstone-vein'] ?? []).includes(galaxyOuterOnly.ore)) {
      issue(`minerals.archetypeExclude must keep ${galaxyOuterOnly.ore} out of tetherstone-vein systems`)
    }
    const cap = m.stackCap
    if (m.signature.mult > cap) issue('minerals.signature.mult must not exceed stackCap')
    if (m.signature.secondaryMult > cap) issue('minerals.signature.secondaryMult must not exceed stackCap')
    if (m.hotspot.mult > cap) issue('minerals.hotspot.mult must not exceed stackCap')
    if (m.guarantee.midTitanium > 0 && !m.signature.eligible.mid.includes('titanium')) {
      issue('minerals.guarantee.midTitanium needs titanium in the mid eligible list')
    }
    if (m.guarantee.deepPlatinum > 0 && !m.signature.eligible.deep.includes('platinum')) {
      issue('minerals.guarantee.deepPlatinum needs platinum in the deep eligible list')
    }
    if ((m.guarantee.deepRefractory ?? 0) > 0 && !((m.refractory.signatureChance.deep ?? 0) > 0 && (m.regionBands.deep[galaxyOuterOnly.ore] ?? 0) > 0)) {
      issue('minerals.guarantee.deepRefractory needs a deep refractory band share and signature chance')
    }
  })

const galaxyRegionSchema = z.object({
  id: z.enum(galaxyRegionIds),
  label: z.string().min(1),
  count: z.number().int().positive(),
  rMin: z.number().nonnegative(),
  rMax: positive,
  /** Share of the region's systems reachable from home over online relays at the start (D-037). */
  onlineReach: z.number().min(0).max(1),
  /** Overrides placement.minSpacing for this region; a pair of systems keeps the looser of its two spacings. */
  minSpacing: positive.optional(),
  /** When set, one system per equal angular sector, placed within this central fraction of its sector (D-037). */
  angularJitter: z.number().min(0).max(1).optional(),
})

export const galaxyConfigSchema = z.object({
  comment: z.string().optional(),
  regions: z
    .array(galaxyRegionSchema)
    .length(galaxyRegionIds.length)
    .refine((rs) => rs.every((r, i) => r.id === galaxyRegionIds[i]), { message: 'regions must be listed gate, mid, deep, rim' })
    .refine((rs) => rs.every((r, i) => r.rMin < r.rMax && (i === 0 || r.rMin === rs[i - 1].rMax)), {
      message: 'region bands must be contiguous and increasing',
    }),
  placement: z.object({
    zJitter: z.number().nonnegative(),
    minSpacing: positive,
    maxTries: z.number().int().positive(),
    relaxFactor: z.number().gt(0).lt(1),
  }),
  relays: z.object({
    targetAvgDegree: positive,
    degreeSlack: z.number().nonnegative(),
    maxDegree: z.number().int().min(2),
    crossLinks: galaxyIntRange,
    longRelays: galaxyIntRange,
    longMinHops: z.number().int().min(2),
    longMaxLength: positive,
    /** No relay may pass closer than this (map units) to a system it doesn't connect (D-037). */
    clearance: z.number().nonnegative(),
    /** Chance a relay between two systems reachable online is also online (a loop in the online network). */
    onlineLoopChance: z.number().min(0).max(1),
    /** Chance a relay between two unreachable systems is online (a working island cut off from home). */
    onlineIslandChance: z.number().min(0).max(1),
  }),
  stars: z.record(
    z.enum(galaxyStarClasses),
    z.object({ weight: galaxyWeight, color: z.string().regex(/^#[0-9a-f]{6}$/i), luminosity: positive }),
  ),
  archetypeWeights: z
    .record(z.enum(galaxyRegionIds), z.record(z.enum(galaxyArchetypes), galaxyWeight))
    .refine((w) => galaxyRegionIds.every((r) => galaxyShare(w[r], 'tetherstone-vein') <= 0.04), {
      message: 'tetherstone-vein must stay at or below 4% in every region',
    })
    .refine((w) => w.gate['tetherstone-vein'] === 0, { message: 'no tetherstone veins in the Gate Cluster' })
    .refine((w) => w.gate['descendant-enclave'] === 0 && w.mid['descendant-enclave'] === 0, {
      message: 'descendant enclaves appear in deep/rim only',
    }),
  chokepointBonus: z.number().nonnegative(),
  /**
   * Every galaxy has at least `min` tetherstone-vein systems (D-086). A post-pass converts non-anchor systems in
   * regions with a positive vein weight (so never the Gate Cluster), preferring `preferRegions`.
   */
  veinGuarantee: z.object({
    comment: z.string().optional(),
    min: z.number().int().nonnegative(),
    preferRegions: z
      .array(z.enum(galaxyRegionIds))
      .min(1)
      .refine((rs) => !rs.includes('gate'), { message: 'veins are never guaranteed in the Gate Cluster' }),
  }),
  archetypes: z
    .record(
      z.enum(galaxyArchetypes),
      z.object({
        reasons: galaxyReasonList,
        minFields: z.partialRecord(z.enum(galaxyFieldKinds), z.number().int().nonnegative()).optional(),
        minGasGiants: z.number().int().nonnegative().optional(),
        oreMult: galaxyOreWeights.optional(),
        oreAdd: galaxyOreWeights.optional(),
        poiMult: z.partialRecord(z.enum(galaxyPoiKinds), galaxyWeight).optional(),
        extraPois: galaxyPoiCounts.optional(),
      }),
    )
    .refine((a) => Object.entries(a).every(([k, v]) => k === 'tetherstone-vein' || !v.oreAdd?.tetherstone), {
      message: 'only the tetherstone-vein archetype may add tetherstone',
    }),
  anchors: z.record(
    z.enum(galaxyAnchorIds),
    z.object({
      name: z.string().min(1),
      comment: z.string().optional(),
      archetype: z.enum(galaxyArchetypes).optional(),
      reasons: galaxyReasonList,
      extraPois: galaxyPoiCounts.optional(),
      /**
       * Online preference when picking the anchor's system: p > 0 picks among systems reachable online from home with
       * chance p; 1 also guarantees it (a route is opened). p < 0 picks among unreachable systems with chance -p.
       */
      onlineBias: z.number().min(-1).max(1).optional(),
      /** A trace ore laced into 1–n of the anchor system's asteroid fields at a fixed share. */
      trace: z
        .object({ ore: z.enum(galaxyOres), weight: z.number().gt(0).max(galaxyTraceMaxWeight), fields: galaxyIntRange })
        .optional(),
      /**
       * A guaranteed hero body (D-TBD game-scale planets): placed from the anchor's first field centre (the home
       * cluster) along `direction` (generator frame, normalised on use) at the distance where its disc spans
       * `angularDiameterDeg`. When that spot is too close to the star, `direction` is yawed by the `yawTryDeg` steps.
       */
      hero: z
        .object({
          kind: z.enum(['rocky', 'icy', 'lava', 'gas']),
          radiusM: positive,
          angularDiameterDeg: z.number().gt(0).lt(90),
          direction: z.tuple([z.number(), z.number(), z.number()]),
          yawTryDeg: z.array(z.number()).min(1),
          rings: z.boolean(),
          rotationS: positive,
        })
        .optional(),
    }),
  ).refine((a) => Object.entries(a).every(([k, v]) => k === 'home-gate' || v.trace?.ore !== 'tetherstone'), {
    message: 'only the home gate may carry trace tetherstone (D-037)',
  }),
  bodies: z.object({
    comment: z.string().optional(),
    count: galaxyIntRange,
    gasChance: z.number().min(0).max(1),
    ringChance: z.number().min(0).max(1),
    otherKinds: z.object({ rocky: galaxyWeight, icy: galaxyWeight, lava: galaxyWeight }),
    /** The hard orbit band (m): every body orbit is clamped into it. */
    orbitM: galaxyNumRange,
    /** The innermost orbit (m) around a luminosity-1 star, scaled by √luminosity (D-TBD inner orbits). */
    firstOrbitM: galaxyNumRange,
    /** Each orbit is the previous one × a ratio drawn from this range (Titius–Bode-like), compressed to fit the band. */
    orbitRatio: galaxyNumRange.refine(([lo]) => lo > 1, { message: 'orbit ratios must exceed 1' }),
    minOrbitGapM: positive,
    /**
     * Clearance (m) kept between the outer extents of neighbouring bodies (radius × ringExtentFactor with rings,
     * × atmosphereExtentFactor without); orbits are pushed apart to keep it (D-TBD game-scale planets).
     */
    extentMarginM: z.number().nonnegative(),
    /** Ring outer radius / body radius; at least scaled.json rings.outer (checked in a test). */
    ringExtentFactor: z.number().min(1),
    /** Atmosphere / halo radius / body radius for a body without rings. */
    atmosphereExtentFactor: z.number().min(1),
    /** Innermost distance from the star (× firstOrbitM[0] × √luminosity) a hero body may sit at. */
    heroStarClearanceFactor: positive,
    gasRadiusM: galaxyNumRange,
    otherRadiusM: galaxyNumRange,
    gasRotationS: galaxyNumRange,
    otherRotationS: galaxyNumRange,
  }),
  fields: z.object({
    asteroid: galaxyIntRange,
    iceGas: galaxyIntRange,
    iceGasKinds: z.object({ ice: galaxyWeight, gas: galaxyWeight }),
    orbitM: galaxyNumRange,
    /** The orbit is lo + (hi − lo) × u^radialBias (u uniform): above 1 pulls fields toward the star. */
    radialBias: positive,
    zM: z.number().nonnegative(),
    radiusM: galaxyNumRange,
    ores: z
      .record(z.enum(galaxyFieldKinds), galaxyOreWeights)
      .refine((o) => Object.values(o).every((w) => !w.tetherstone), { message: 'tetherstone only comes from an archetype oreAdd' })
      .refine((o) => (o.asteroid.ice ?? 0) > 0, { message: 'the asteroid mix keeps ice above 0 (fuel cells need it)' }),
  }),
  minerals: galaxyMineralsSchema,
  pois: z.object({
    count: galaxyIntRange,
    weights: z.record(z.enum(galaxyPoiKinds), galaxyWeight),
    hiddenChance: z.record(z.enum(galaxyPoiKinds), z.number().min(0).max(1)),
    reasons: z.partialRecord(z.enum(galaxyPoiKinds), z.enum(galaxyReasons)),
    radiusM: galaxyNumRange,
    /** As fields.radialBias, for the POI radius. */
    radialBias: positive,
    zM: z.number().nonnegative(),
  }),
  density: z.object({
    minPois: z.number().int().nonnegative(),
    minReasons: z.number().int().nonnegative(),
    maxRetries: z.number().int().nonnegative(),
    fallbackPoi: z.enum(galaxyPoiKinds),
  }),
  systemRadiusM: positive,
  names: z.object({
    comment: z.string().optional(),
    onsets: z.array(z.string()).min(1),
    innerOnsets: z.array(z.string().min(1)).min(1),
    nuclei: z.array(z.string().min(1)).min(1),
    innerCodas: z.array(z.string()).min(1),
    codas: z.array(z.string()).min(1),
    syllables: galaxyIntRange.refine(([lo]) => lo >= 1, { message: 'names need at least one syllable' }),
    length: galaxyIntRange,
    maxLength: z.number().int().positive(),
    maxRetries: z.number().int().nonnegative(),
  }),
})
  .superRefine((c, ctx) => {
    const issue = (message: string): void => {
      ctx.addIssue({ code: 'custom', message })
    }
    const b = c.bodies
    if (b.orbitM[1] > c.systemRadiusM) issue('body orbits must fit inside systemRadiusM')
    const maxLum = Math.max(...Object.values(c.stars).map((st) => st.luminosity))
    const firstMax = Math.min(b.orbitM[1], Math.max(b.orbitM[0], b.firstOrbitM[1] * Math.sqrt(maxLum)))
    const maxExtent = Math.max(b.gasRadiusM[1], b.otherRadiusM[1]) * Math.max(b.ringExtentFactor, b.atmosphereExtentFactor)
    const maxGap = Math.max(b.minOrbitGapM, 2 * maxExtent + b.extentMarginM)
    if (firstMax + (b.count[1] - 1) * maxGap > b.orbitM[1]) issue('the orbit band cannot fit the max body count at the largest orbit gap')
    if (Math.hypot(c.fields.orbitM[1], c.fields.zM) + c.fields.radiusM[1] > c.systemRadiusM) issue('fields must fit inside systemRadiusM')
    if (Math.hypot(c.pois.radiusM[1], c.pois.zM) > c.systemRadiusM) issue('points of interest must fit inside systemRadiusM')
    if (c.density.minReasons > 0 && !c.pois.reasons[c.density.fallbackPoi]) issue('density.fallbackPoi must give a reason to visit')
    const positiveTotal = (ws: Record<string, number>): boolean => Object.values(ws).some((w) => w > 0)
    if (!positiveTotal(Object.fromEntries(Object.entries(c.stars).map(([k, s]) => [k, s.weight])))) issue('some star weight must be positive')
    if (!galaxyRegionIds.every((r) => positiveTotal(c.archetypeWeights[r]))) issue('every region needs a positive archetype weight')
    if (!positiveTotal(c.pois.weights)) issue('some POI weight must be positive')
    const veinRegions = c.regions.filter((r) => c.archetypeWeights[r.id]['tetherstone-vein'] > 0)
    if (c.veinGuarantee.preferRegions.some((r) => !veinRegions.some((v) => v.id === r))) {
      issue('veinGuarantee.preferRegions must have a positive tetherstone-vein weight')
    }
    const veinSlots = veinRegions.reduce((n, r) => n + r.count, 0) - Object.keys(c.anchors).length
    if (c.veinGuarantee.min > veinSlots) issue('veinGuarantee.min exceeds the non-anchor systems that can hold a vein')
    if (!positiveTotal(c.fields.iceGasKinds)) issue('some ice/gas field weight must be positive')
    const anchorNames = Object.values(c.anchors).map((a) => a.name.toLowerCase())
    if (new Set(anchorNames).size !== anchorNames.length) issue('anchor names must be unique')
    if (Object.values(c.anchors).some((a) => a.name.length > c.names.maxLength)) issue('anchor names must fit names.maxLength')
    if (c.names.length[1] > c.names.maxLength) issue('names.length max must not exceed names.maxLength')
    for (const [id, a] of Object.entries(c.archetypes)) {
      const m = a.minFields ?? {}
      if ((m.asteroid ?? 0) > c.fields.asteroid[1]) issue(`${id}: minFields.asteroid exceeds fields.asteroid max`)
      if ((m.ice ?? 0) + (m.gas ?? 0) > c.fields.iceGas[1]) issue(`${id}: minFields ice + gas exceeds fields.iceGas max`)
      if ((a.minGasGiants ?? 0) > b.count[0]) issue(`${id}: minGasGiants exceeds the minimum body count`)
    }
  })

export type GalaxyConfig = z.infer<typeof galaxyConfigSchema>

/** Parses and validates the galaxy generator tuning. Throws a ZodError that names the bad field. */
export function loadGalaxyConfig(): GalaxyConfig {
  return galaxyConfigSchema.parse(galaxyJson)
}
